import { useState } from "react";
import { toast } from "sonner";
import { Copy, Share2, MessageCircle, Printer, RefreshCw, Loader2, Mail, Send } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { api } from "@/lib/api";
import { joinUrl, copyText, shareLink, whatsappHref } from "./joinLink";

export const QrCard = ({ link }) => (
  <div className="flex flex-col sm:flex-row items-center gap-4 rounded-xl border border-border p-4" data-testid="join-qr-card">
    <div className="rounded-lg bg-white p-2 border border-border shrink-0" data-testid="join-qr-img"><QRCodeSVG value={joinUrl(link.code)} size={132} level="M" /></div>
    <div className="min-w-0 w-full space-y-2">
      <p className="text-sm font-medium">New staff scan this with their phone camera, or open the link.</p>
      <div className="flex gap-2">
        <Input readOnly value={joinUrl(link.code)} className="font-mono text-xs" data-testid="join-link-input" onFocus={(e) => e.target.select()} />
        <Button type="button" variant="outline" size="icon" onClick={() => copyText(joinUrl(link.code))} aria-label="Copy link" data-testid="join-link-copy"><Copy className="w-4 h-4" /></Button>
      </div>
    </div>
  </div>
);

export const ShareRow = ({ link }) => (
  <div className="flex flex-wrap gap-2">
    <Button type="button" onClick={() => shareLink(link)} className="rounded-full bg-primary hover:bg-primary/90 text-white" data-testid="join-share-btn"><Share2 className="w-4 h-4 mr-2" />Share link</Button>
    <Button type="button" variant="outline" asChild className="rounded-full">
      <a href={whatsappHref(link)} target="_blank" rel="noreferrer" data-testid="join-whatsapp-btn"><MessageCircle className="w-4 h-4 mr-2" />WhatsApp</a>
    </Button>
    <Button type="button" variant="outline" asChild className="rounded-full">
      <a href={`/join/${link.code}/poster`} target="_blank" rel="noreferrer" data-testid="join-poster-btn"><Printer className="w-4 h-4 mr-2" />Print QR poster</a>
    </Button>
  </div>
);

export const EmailInvite = ({ depotId }) => {
  const [f, setF] = useState({ email: "", name: "" });
  const [busy, setBusy] = useState(false);
  const send = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { const r = await api.emailJoinLink({ ...f, depot_id: depotId || "" }); toast.success(`Invite sent to ${r.to}`); setF({ email: "", name: "" }); }
    catch (err) { toast.error(err?.response?.data?.detail || "Could not send the invite"); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={send} className="rounded-xl border border-border p-4 space-y-3" data-testid="join-email-form">
      <p className="text-sm font-medium flex items-center gap-2"><Mail className="w-4 h-4 text-primary" />Email the invite</p>
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="space-y-1.5"><Label htmlFor="ji-email">Their email *</Label>
          <Input id="ji-email" type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="new.staff@email.com" data-testid="join-email-input" /></div>
        <div className="space-y-1.5"><Label htmlFor="ji-name">Their name</Label>
          <Input id="ji-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Optional" data-testid="join-email-name" /></div>
      </div>
      <Button type="submit" disabled={busy} variant="outline" className="rounded-full" data-testid="join-email-send">
        {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}Send invite
      </Button>
    </form>
  );
};

export const ResetLink = ({ busy, onReset }) => (
  <AlertDialog>
    <AlertDialogTrigger asChild>
      <button type="button" disabled={busy} className="text-xs font-medium text-muted-foreground hover:text-destructive inline-flex items-center gap-1.5" data-testid="join-reset-btn">
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Reset link
      </button>
    </AlertDialogTrigger>
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>Make a new join link?</AlertDialogTitle>
        <AlertDialogDescription>The old link, QR code and any printed posters stop working. People already in your crew stay in it.</AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel data-testid="join-reset-cancel">Cancel</AlertDialogCancel>
        <AlertDialogAction onClick={onReset} data-testid="join-reset-confirm">Make new link</AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);
